/**
 * Destino post-login / resume — parity con shell `postLoginRedirect.js`.
 *
 * Jerarquía módulo de inicio (backend):
 * 1) usuarios_perfiles.id_modulo_inicio (override usuario)
 * 2) perfiles.id_modulo_inicio (default perfil)
 * 3) /dashboard
 *
 * Resume de sesión: solo modulo_inicio (como `navigateIfSessionActive`).
 * Handoff portal: post_login_path *meaningful* (no hub) → modulo_inicio → /dashboard.
 * `/dashboard` y `/` en post_login_path = sin preferencia (no pisan modulo_inicio).
 */

const DEFAULT_POST_LOGIN_PATH = '/dashboard';

/**
 * Path portal que expresa destino real (no el hub genérico del seed).
 * @param {unknown} raw
 * @returns {boolean}
 */
export function isMeaningfulPortalPostLoginPath(raw) {
  const trimmed = String(raw ?? '').trim();
  if (!trimmed.startsWith('/') || trimmed.startsWith('//')) return false;
  const path = trimmed.split('?')[0].split('#')[0].replace(/\/+$/u, '') || '/';
  if (path === '/' || path === '/dashboard') return false;
  return path.length > 1;
}

export function normalizeMenuSlug(value) {
  return String(value ?? '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

/**
 * @param {unknown} res
 * @returns {{ id: number, url: string|null, nombre: string|null }|null}
 */
export function parseModuloInicio(res) {
  const root = res?.data ?? res ?? {};
  const raw = root.modulo_inicio ?? root.moduloInicio ?? null;
  if (!raw || typeof raw !== 'object') return null;

  const id = Number(raw.id);
  const url = raw.url != null ? String(raw.url).trim() || null : null;
  const nombre = raw.nombre != null ? String(raw.nombre).trim() || null : null;

  if (!Number.isInteger(id) || id <= 0) return null;
  return { id, url, nombre };
}

/**
 * @param {{ url?: string|null, nombre?: string|null }|null|undefined} moduloInicio
 * @returns {string|null}
 */
export function resolvePathFromModuloInicio(moduloInicio) {
  if (!moduloInicio) return null;

  const rawUrl = String(moduloInicio.url ?? '').trim();
  if (!rawUrl) return null;

  if (rawUrl.startsWith('/')) {
    const path = rawUrl.split('?')[0].split('#')[0];
    return path && path.length > 1 ? path : null;
  }

  const slug = normalizeMenuSlug(rawUrl);
  return slug ? `/${slug}` : null;
}

/**
 * Resume / sesión activa — NO usa portal.post_login_path (handoff stale).
 * @param {unknown} res
 * @param {{ fallback?: string, fromProtectedRoute?: string|null }} [options]
 * @returns {string}
 */
export function resolvePostLoginPath(res, options = {}) {
  const {
    fallback = DEFAULT_POST_LOGIN_PATH,
    fromProtectedRoute = null,
  } = options;

  if (fromProtectedRoute) return fromProtectedRoute;

  const modulo = parseModuloInicio(res);
  const path = resolvePathFromModuloInicio(modulo);
  return path || fallback;
}

/**
 * Tras login/handoff desde LoginPortal (opaque o same-origin).
 * @param {unknown} res
 * @returns {string}
 */
export function resolvePortalHandoffPostLoginPath(res) {
  const root = res?.data ?? res ?? {};
  const portalPath = root?.portal?.post_login_path;
  if (isMeaningfulPortalPostLoginPath(portalPath)) {
    const path = String(portalPath).trim().split('?')[0].split('#')[0];
    return path.replace(/\/+$/u, '') || path;
  }
  return resolvePostLoginPath(res);
}

export { DEFAULT_POST_LOGIN_PATH };
